import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { ThreeDots } from "react-loader-spinner";
import ClockIcon from "../../../icons/ClockIcon";
import { icons } from "../../../svg";
import { formatOriginNameDisplay } from "../multiLocQuoteDetailsPanel/multiLocQuoteDetailsUtils";
import {
  RequestTariffSectionProps,
  PublicTariffStatus,
} from "../../../types/IRailRateRequest";
import type { MissingRailFreightSectionItem } from "../../../types/IMissingRailFreight";

const parseTariffRateNumber = (value: unknown): number | null => {
  if (value == null) return null;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  const normalizedRate = String(value).replace(/[^0-9.-]/g, "").trim();
  if (!normalizedRate) return null;
  const parsedRate = Number(normalizedRate);
  return Number.isFinite(parsedRate) ? parsedRate : null;
};

interface OriginRowFlagsParams {
  requestedRailRateByOriginKey: Record<string, boolean>;
  failedRailRateRequestByOriginKey: Record<string, boolean>;
  loadingRailRateRequestByOriginKey: Record<string, boolean>;
  publicTariffStatusByOriginKey: Record<string, PublicTariffStatus>;
  publicTariffRateByOriginKey: Record<string, string>;
  localLoadingByOriginKey: Record<string, boolean>;
  resolvedPublicTariffRateByOriginKey: Record<string, number>;
}

// Derives all the display flags/labels for a single origin row from raw request/status maps
const getOriginRowFlags = (
  railOrigin: MissingRailFreightSectionItem,
  {
    requestedRailRateByOriginKey,
    failedRailRateRequestByOriginKey,
    loadingRailRateRequestByOriginKey,
    publicTariffStatusByOriginKey,
    publicTariffRateByOriginKey,
    localLoadingByOriginKey,
    resolvedPublicTariffRateByOriginKey,
  }: OriginRowFlagsParams,
) => {
  const hasExistingRailFreight = !!railOrigin.quote?.freight;
  const isRailRateRequested =
    !!requestedRailRateByOriginKey[railOrigin.originKey] ||
    (railOrigin?.railValidation as any)?.railRateRequested === true ||
    (railOrigin as any)?.railRateRequested === true ||
    hasExistingRailFreight;

  const isLocalLoading = !!localLoadingByOriginKey[railOrigin.originKey];

  const isRailRateRequestFailed =
    !!failedRailRateRequestByOriginKey[railOrigin.originKey] ||
    (railOrigin?.railValidation as any)?.railRateRequestFailed === true ||
    (railOrigin as any)?.railRateRequestFailed === true;

  const isRailRateValidationFailed =
    (railOrigin?.railValidation as any)?.railRateRequestFailed === true;

  const isRailRateRequestLoading =
    !!loadingRailRateRequestByOriginKey[railOrigin.originKey];

  const publicTariffStatus = publicTariffStatusByOriginKey[railOrigin.originKey];
  const publicTariffRate =
    publicTariffRateByOriginKey[railOrigin.originKey] ||
    railOrigin.quote?.freight?.totalFreight;

  const publicTariffRateNumber = parseTariffRateNumber(publicTariffRate);
  const resolvedPublicTariffRateNumber =
    publicTariffRateNumber ??
    resolvedPublicTariffRateByOriginKey[railOrigin.originKey] ??
    null;

  const isFreightMissing = resolvedPublicTariffRateNumber == null;

  // Fixes secondary flicker by checking that data is missing alongside checking status
  const isChecking = (publicTariffStatus === "checking" || isLocalLoading) && isFreightMissing;

  const isFailedMissingFreightNonCompetitor =
    isFreightMissing && isRailRateRequestFailed && !railOrigin.competitor;

  const isFailedMissingFreightCompetitor =
    isFreightMissing && isRailRateRequestFailed && !!railOrigin.competitor;

  const showNotAvailableWithRetry =
    (isFreightMissing && !isRailRateRequestFailed) || isChecking;

  const showNotFoundWithRetry =
    isFreightMissing &&
    !showNotAvailableWithRetry &&
    (isFailedMissingFreightCompetitor ||
    (!isFailedMissingFreightNonCompetitor &&
     !isChecking &&
     (publicTariffStatus === "rate-not-found-retry" ||
       (railOrigin.competitor && publicTariffStatus === "rate-not-found") ||
       isRailRateRequestFailed)));

  const showNotAvailableTariff =
    !showNotAvailableWithRetry &&
    !isFailedMissingFreightNonCompetitor &&
    !isFailedMissingFreightCompetitor &&
    !isChecking &&
    publicTariffStatus === "rate-not-found" &&
    !isRailRateRequestFailed &&
    !isRailRateValidationFailed &&
    !railOrigin.competitor;

  const showNotFoundNoRetry =
    !isChecking &&
    (isFailedMissingFreightNonCompetitor ||
    (!showNotAvailableWithRetry &&
      isRailRateValidationFailed &&
      !railOrigin.competitor &&
      !isRailRateRequestFailed &&
      publicTariffStatus !== "rate-not-found-retry" &&
      publicTariffStatus !== "rate-not-found" &&
      publicTariffStatus !== "rate-not-available-retry"));

  const shouldShowRequestButton = !isRailRateRequested;
  const shouldShowRequestedCell = isRailRateRequested;

  // Keeps row mounted instantly upon click and holds it open through prop shifts
  // Only show the tariff row once the rail rate has been requested
  const shouldShowPublicTariffRow =
    isRailRateRequested &&
    (isLocalLoading ||
      isRailRateRequested ||
      isChecking ||
      isRailRateRequestFailed ||
      isRailRateValidationFailed ||
      publicTariffStatus === "external-rate" ||
      resolvedPublicTariffRateNumber != null);

  const notAvailableLabel = isRailRateRequestFailed ? "Request Failed" : "Tariff Rate Not Available";

  let notFoundWithRetryLabel = "Tariff Rate Not Found";
  if (isFailedMissingFreightCompetitor) {
    notFoundWithRetryLabel = "Tariff Rate Not Found";
  } else if (isRailRateRequestFailed) {
    notFoundWithRetryLabel = "Request Failed";
  }

  return {
    isRailRateRequestFailed,
    isRailRateRequestLoading,
    publicTariffRate,
    resolvedPublicTariffRateNumber,
    isChecking,
    isFailedMissingFreightCompetitor,
    showNotAvailableWithRetry,
    showNotFoundWithRetry,
    showNotAvailableTariff,
    showNotFoundNoRetry,
    shouldShowRequestButton,
    shouldShowRequestedCell,
    shouldShowPublicTariffRow,
    notAvailableLabel,
    notFoundWithRetryLabel,
  };
};

type OriginRowFlags = ReturnType<typeof getOriginRowFlags>;

interface PublicTariffStatusRowProps {
  originKey: string;
  flags: OriginRowFlags;
  countdownSec: number;
  isCheckAgainBlocked: boolean;
  isRetryActionBlocked: boolean;
  onRequestClick: () => void;
  onCheckAgainClick: () => void;
}

// Renders the "public tariff rate" status area (found/not-found/not-available variants) for one row
const PublicTariffStatusRow: React.FC<PublicTariffStatusRowProps> = ({
  originKey,
  flags,
  countdownSec,
  isCheckAgainBlocked,
  isRetryActionBlocked,
  onRequestClick,
  onCheckAgainClick,
}) => {
  const {
    isRailRateRequestFailed,
    isFailedMissingFreightCompetitor,
    resolvedPublicTariffRateNumber,
    isChecking,
    showNotAvailableTariff,
    showNotFoundNoRetry,
    showNotFoundWithRetry,
    showNotAvailableWithRetry,
    notAvailableLabel,
    notFoundWithRetryLabel,
    publicTariffRate,
  } = flags;
  const retryActionLabel = "Try Again";

  return (
    <div className="request-tratic-data-public-tariff-row">
      {resolvedPublicTariffRateNumber != null && !isChecking && (
        <div id={`public-tariff-rate-${originKey}`} className="request-tratic-data-public-tariff-result request-tratic-data-public-tariff-result-found">
          <div className="request-tratic-data-public-tariff-rate-left">
            <img src={icons.ExclamationMarkTriangle} alt="Public tariff" className="request-tratic-data-public-tariff-rate-icon" />
            <span className="request-tratic-data-public-tariff-rate-label">Public Tariff Rate</span>
          </div>
          <span className="request-tratic-data-public-tariff-rate-value">
            {resolvedPublicTariffRateNumber != null ? `$${resolvedPublicTariffRateNumber.toFixed(2)}` : publicTariffRate}
          </span>
        </div>
      )}

      {showNotAvailableTariff && (
        <div id="public-tariff-result-not-available" className={`request-tratic-data-public-tariff-result request-tratic-data-public-tariff-result-not-found ${isRailRateRequestFailed ? "request-tratic-data-public-tariff-result-request-failed" : ""}`}>
          <div className="request-tratic-data-public-tariff-rate-left">
            <img src={icons.ExclamationMarkTriangle} alt="Public tariff" className="request-tratic-data-public-tariff-rate-icon" />
            <span className="request-tratic-data-public-tariff-rate-label">{notAvailableLabel}</span>
          </div>
        </div>
      )}

      {showNotFoundNoRetry && (
        <div id={`public-tariff-result-not-found-no-retry-${originKey}`} className="request-tratic-data-public-tariff-result request-tratic-data-public-tariff-result-not-found">
          <div className="request-tratic-data-public-tariff-rate-left">
            <img src={icons.ExclamationMarkTriangle} alt="Public tariff" className="request-tratic-data-public-tariff-rate-icon" />
            <span className="request-tratic-data-public-tariff-rate-label">Tariff Rate Not Found</span>
          </div>
        </div>
      )}

      {showNotFoundWithRetry && (
        <div className="request-tratic-data-public-tariff-stack">
          <div
            id={`public-tariff-rate-not-found-${originKey}`}
            className={`request-tratic-data-public-tariff-result request-tratic-data-public-tariff-result-not-found ${isRailRateRequestFailed ? "request-tratic-data-public-tariff-result-request-failed" : ""}`}
            style={{ display: "flex", flexDirection: "row", flexWrap: "nowrap", justifyContent: "space-between", alignItems: "center", width: "100%" }}
          >
            <div className="request-tratic-data-public-tariff-rate-left">
              <img src={icons.ExclamationMarkTriangle} alt="Public tariff" className="request-tratic-data-public-tariff-rate-icon" />
              <span className="request-tratic-data-public-tariff-rate-label">{notFoundWithRetryLabel}</span>
            </div>
            <button
              id={`public-tariff-check-again-button-${originKey}`}
              type="button"
              className={`request-tratic-data-public-tariff-action ${isRailRateRequestFailed ? "request-tratic-data-public-tariff-action-try-again" : ""}`}
              onClick={isRailRateRequestFailed || isFailedMissingFreightCompetitor ? onRequestClick : onCheckAgainClick}
              disabled={isRetryActionBlocked}
              style={{ flexShrink: 0, whiteSpace: "nowrap" }}
            >
              <img src={icons.RefreshButtonIconBlue} alt="Try again" className="request-tratic-data-public-tariff-action-icon" />
              <span>{retryActionLabel}</span>
            </button>
          </div>
          {!isRailRateRequestFailed && isCheckAgainBlocked && (
            <div id={`public-tariff-check-again-error-${originKey}`} className="request-tratic-data-public-tariff-rate-label">
              You have tried three times, please wait 5 minutes to try again.
            </div>
          )}
        </div>
      )}

      {showNotAvailableWithRetry && (
        <div className="request-tratic-data-public-tariff-stack">
          <div
            id={`public-tariff-rate-not-available-${originKey}`}
            className="request-tratic-data-public-tariff-result request-tratic-data-public-tariff-result-not-found"
            style={{ display: "flex", flexDirection: "row", flexWrap: "nowrap", justifyContent: "space-between", alignItems: "center", width: "100%" }}
          >
            <div className="request-tratic-data-public-tariff-rate-left">
              <img src={icons.ExclamationMarkTriangle} alt="Public tariff" className="request-tratic-data-public-tariff-rate-icon" />
              <span className="request-tratic-data-public-tariff-rate-label">
                {isChecking ? "Looking up tariff rate..." : "Tariff Rate Not Available"}
              </span>
            </div>
            {isChecking ? (
              <span className="request-tratic-data-public-tariff-countdown">
                -{countdownSec}s remaining
              </span>
            ) : (
              <button
                id={`public-tariff-check-again-button-not-available-${originKey}`}
                type="button"
                className="request-tratic-data-public-tariff-action"
                onClick={onCheckAgainClick}
                disabled={isCheckAgainBlocked}
                style={{ flexShrink: 0, whiteSpace: "nowrap" }}
              >
                <img src={icons.RefreshButtonIconBlue} alt="Check again" className="request-tratic-data-public-tariff-action-icon" />
                <span>Check Again</span>
              </button>
            )}
          </div>
          {!isChecking && isCheckAgainBlocked && (
            <div id={`public-tariff-check-again-error-not-available-${originKey}`} className="request-tratic-data-public-tariff-rate-label">
              You have tried three times, please wait 5 minutes to try again.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const RequestTariffSection: React.FC<RequestTariffSectionProps> = ({
  originsMissingRailFreight,
  requestedRailRateByOriginKey,
  failedRailRateRequestByOriginKey,
  loadingRailRateRequestByOriginKey,
  publicTariffStatusByOriginKey,
  publicTariffRateByOriginKey,
  onRequestMultiple,
  onRequestSingleOrigin,
  onCheckPublicTariff,
}) => {
  const [checkAgainAttemptsByOriginKey, setCheckAgainAttemptsByOriginKey] =
    useState<Record<string, number>>({});
  const [checkAgainBlockedUntilByOriginKey, setCheckAgainBlockedUntilByOriginKey] =
    useState<Record<string, number>>({});
  const [localLoadingByOriginKey, setLocalLoadingByOriginKey] =
    useState<Record<string, boolean>>({});
  const [requestedFromButtonByOriginKey, setRequestedFromButtonByOriginKey] =
    useState<Record<string, boolean>>({});
  const [showRailRateRequestFailedSheet, setShowRailRateRequestFailedSheet] =
    useState(false);
  const [resolvedPublicTariffRateByOriginKey, setResolvedPublicTariffRateByOriginKey] =
    useState<Record<string, number>>({});

  const checkStartedAtRef = useRef<Record<string, number>>({});
  const [, forceCountdownRerender] = useState(0);

  const retryLimit = 4;
  const retryCooldownMs = 5 * 60 * 1000;

  useEffect(() => {
    setLocalLoadingByOriginKey((prev) => {
      const updatedLocalLoading = { ...prev };
      let hasChanges = false;

      Object.keys(updatedLocalLoading).forEach((key) => {
        if (
          requestedRailRateByOriginKey[key] || 
          publicTariffStatusByOriginKey[key] === "checking" ||
          publicTariffStatusByOriginKey[key] === "external-rate" ||
          failedRailRateRequestByOriginKey[key]
        ) {
          delete updatedLocalLoading[key];
          hasChanges = true;
        }
      });

      return hasChanges ? updatedLocalLoading : prev;
    });

    setRequestedFromButtonByOriginKey((prev) => {
      const next = { ...prev };
      let hasChanges = false;

      Object.keys(next).forEach((key) => {
        if (
          requestedRailRateByOriginKey[key] ||
          publicTariffStatusByOriginKey[key] === "checking" ||
          publicTariffStatusByOriginKey[key] === "external-rate"
        ) {
          delete next[key];
          hasChanges = true;
        }
      });

      return hasChanges ? next : prev;
    });
  }, [
    requestedRailRateByOriginKey,
    publicTariffStatusByOriginKey,
    failedRailRateRequestByOriginKey,
  ]);

  useEffect(() => {
    const failedRequestKeys = Object.keys(requestedFromButtonByOriginKey).filter(
      (originKey) =>
        requestedFromButtonByOriginKey[originKey] &&
        failedRailRateRequestByOriginKey[originKey],
    );

    if (!failedRequestKeys.length) {
      return;
    }

    setShowRailRateRequestFailedSheet(true);
    setRequestedFromButtonByOriginKey((prev) => {
      const next = { ...prev };
      failedRequestKeys.forEach((originKey) => {
        delete next[originKey];
      });
      return next;
    });
  }, [failedRailRateRequestByOriginKey, requestedFromButtonByOriginKey]);

  useEffect(() => {
    const checkingKeys = Object.keys(publicTariffStatusByOriginKey).filter(
      (k) => publicTariffStatusByOriginKey[k] === "checking",
    );
    checkingKeys.forEach((key) => {
      if (!checkStartedAtRef.current[key]) {
        checkStartedAtRef.current[key] = Date.now();
      }
    });

    Object.keys(checkStartedAtRef.current).forEach((key) => {
      const isChecking = publicTariffStatusByOriginKey[key] === "checking";
      const isRequestInFlight =
        !!localLoadingByOriginKey[key] || !!requestedFromButtonByOriginKey[key];

      if (!isChecking && !isRequestInFlight) {
        delete checkStartedAtRef.current[key];
      }
    });
  }, [
    localLoadingByOriginKey,
    publicTariffStatusByOriginKey,
    requestedFromButtonByOriginKey,
  ]);

  useEffect(() => {
    setResolvedPublicTariffRateByOriginKey((prev) => {
      const next = { ...prev };
      let hasChanges = false;

      const activeOriginKeys = new Set(
        originsMissingRailFreight.map((originEntry) => originEntry.originKey),
      );

      Object.keys(next).forEach((key) => {
        if (!activeOriginKeys.has(key)) {
          delete next[key];
          hasChanges = true;
        }
      });

      originsMissingRailFreight.forEach((originEntry) => {
        const { originKey } = originEntry;
        const parsedRate = parseTariffRateNumber(
          publicTariffRateByOriginKey[originKey],
        );

        if (parsedRate != null && next[originKey] !== parsedRate) {
          next[originKey] = parsedRate;
          hasChanges = true;
        }
      });

      return hasChanges ? next : prev;
    });
  }, [originsMissingRailFreight, publicTariffRateByOriginKey]);

  useEffect(() => {
    const isAnyChecking = Object.values(publicTariffStatusByOriginKey).some(
      (s) => s === "checking",
    );
    const hasActiveCountdown = Object.keys(checkStartedAtRef.current).length > 0;

    if (!isAnyChecking && !hasActiveCountdown) return undefined;
    const interval = setInterval(() => forceCountdownRerender((n) => n + 1), 1000);
    return () => clearInterval(interval);
  }, [
    localLoadingByOriginKey,
    publicTariffStatusByOriginKey,
    requestedFromButtonByOriginKey,
  ]);

  if (!originsMissingRailFreight.length) {
    return null;
  }

  
  const originsWithoutDuplicateCompetitors = originsMissingRailFreight.filter(
    (railOrigin) => {
      if (!railOrigin.competitor) return true;
      return !originsMissingRailFreight.some(
        (other) =>
          !other.competitor &&
          other.originName === railOrigin.originName,
      );
    },
  );

  const sortedOrigins = [...originsWithoutDuplicateCompetitors].sort((a, b) => {
    const aHasRequested =
      !!requestedRailRateByOriginKey[a.originKey] ||
      (a?.railValidation as any)?.railRateRequested === true ||
      (a as any)?.railRateRequested === true ||
      !!a.quote?.freight;
    const bHasRequested =
      !!requestedRailRateByOriginKey[b.originKey] ||
      (b?.railValidation as any)?.railRateRequested === true ||
      (b as any)?.railRateRequested === true ||
      !!b.quote?.freight;

    return Number(bHasRequested) - Number(aHasRequested);
  });

  const renderOriginRow = (railOrigin: (typeof sortedOrigins)[number]) => {
    const flags = getOriginRowFlags(railOrigin, {
      requestedRailRateByOriginKey,
      failedRailRateRequestByOriginKey,
      loadingRailRateRequestByOriginKey,
      publicTariffStatusByOriginKey,
      publicTariffRateByOriginKey,
      localLoadingByOriginKey,
      resolvedPublicTariffRateByOriginKey,
    });

    const now = Date.now();
    const blockedUntil = checkAgainBlockedUntilByOriginKey[railOrigin.originKey] || 0;
    const isCheckAgainBlocked = blockedUntil > now;
    const isRetryActionBlocked = !flags.isRailRateRequestFailed && isCheckAgainBlocked;

    const startedAt = checkStartedAtRef.current[railOrigin.originKey];
    const elapsedSec = startedAt ? Math.floor((Date.now() - startedAt) / 1000) : 0;
    const countdownSec = Math.max(0, 60 - elapsedSec);

    const handleRequestClick = () => {
      const { originKey } = railOrigin;

      if (railOrigin.competitor) {
        if (!checkStartedAtRef.current[originKey]) {
          checkStartedAtRef.current[originKey] = Date.now();
          forceCountdownRerender((n) => n + 1);
        }

        setLocalLoadingByOriginKey((prev) => ({ ...prev, [originKey]: true }));
        setRequestedFromButtonByOriginKey((prev) => ({
          ...prev,
          [originKey]: true,
        }));
      }

      onRequestSingleOrigin(railOrigin);
    };

    const handleCheckAgainClick = () => {
      const { originKey } = railOrigin;
      const nowTs = Date.now();
      const existingBlockedUntil = checkAgainBlockedUntilByOriginKey[originKey] || 0;

      if (existingBlockedUntil > nowTs) return;

      const cooldownExpired = existingBlockedUntil > 0 && existingBlockedUntil <= nowTs;
      const previousAttempts = cooldownExpired ? 0 : checkAgainAttemptsByOriginKey[originKey] || 0;
      const nextAttempts = previousAttempts + 1;

      setCheckAgainAttemptsByOriginKey((prev) => ({ ...prev, [originKey]: nextAttempts }));

      if (nextAttempts >= retryLimit) {
        setCheckAgainBlockedUntilByOriginKey((prev) => ({ ...prev, [originKey]: nowTs + retryCooldownMs }));
        return;
      }

      if (cooldownExpired) {
        setCheckAgainBlockedUntilByOriginKey((prev) => {
          const next = { ...prev };
          delete next[originKey];
          return next;
        });
      }

      onCheckPublicTariff(railOrigin);
    };

    return (
      <div key={`request-tratic-data-${railOrigin.originKey}`} className="request-tratic-data-row">
        <div className="request-tratic-data-top-row">
          <div className={`request-tratic-data-origin-name ${railOrigin.competitor ? "request-tratic-data-origin-name-competitor" : "request-tratic-data-origin-name-koch"}`}>
            {formatOriginNameDisplay(railOrigin.originName)}
          </div>
          {flags.shouldShowRequestButton && (
            <button type="button" className="request-tratic-data-button" onClick={handleRequestClick} disabled={flags.isRailRateRequestLoading}>
              {flags.isRailRateRequestLoading ? (
                <ThreeDots color="#64748B" height={14} width={20} />
              ) : (
                <img src={icons.AddIcon} alt="Request" className="request-tratic-data-button-icon" />
              )}
              <span className="request-tratic-data-button-text">{flags.isRailRateRequestLoading ? "Requesting..." : "Request"}</span>
            </button>
          )}
          {flags.shouldShowRequestedCell && (
            <div className="request-tratic-data-requested-cell">
              <ClockIcon className="request-tratic-data-requested-icon" />
              <span className="request-tratic-data-requested-text">Rail Rate Requested</span>
            </div>
          )}
        </div>

        {flags.shouldShowPublicTariffRow && (
          <PublicTariffStatusRow
            originKey={railOrigin.originKey}
            flags={flags}
            countdownSec={countdownSec}
            isCheckAgainBlocked={isCheckAgainBlocked}
            isRetryActionBlocked={isRetryActionBlocked}
            onRequestClick={handleRequestClick}
            onCheckAgainClick={handleCheckAgainClick}
          />
        )}
      </div>
    );
  };

  return (
    <div id="request-tratic-data" className="request-tratic-data-section">
      <div className="request-tratic-data-header">
        <span className="request-tratic-data-title">Origins Missing Rail Rate</span>
        <button
          type="button"
          className="request-tratic-data-multiple-origin-btn"
          onClick={onRequestMultiple}
        >
          + Request Multiple
        </button>
      </div>
      {sortedOrigins.map(renderOriginRow)}

      {showRailRateRequestFailedSheet && (
        createPortal(
          <div
            id="rail-rate-request-failed-sheet"
            className="rail-rate-request-failed-sheet"
            role="alertdialog"
            aria-labelledby="rail-rate-request-failed-title"
            aria-describedby="rail-rate-request-failed-description"
          >
            <div className="rail-rate-request-failed-handle" />
            <div className="rail-rate-request-failed-content">
              <img
                data-tooltip-id="exclamation-mark-with-circle"
                src={icons.ExclamationMarkWithCircle}
                alt="exclamation-mark-with-circle"
              />
              <div className="rail-rate-request-failed-copy">
                <span
                  id="rail-rate-request-failed-title"
                  className="rail-rate-request-failed-title"
                >
                  Rail Rate Request Failed to Send
                </span>
                <span
                  id="rail-rate-request-failed-description"
                  className="rail-rate-request-failed-description"
                >
                  Try Again Later
                </span>
              </div>
            </div>
            <button
              id="rail-rate-request-failed-close"
              type="button"
              className="rail-rate-request-failed-close"
              onClick={() => setShowRailRateRequestFailedSheet(false)}
            >
              Close
            </button>
          </div>,
          document.body,
        )
      )}
    </div>
  );
};

export default RequestTariffSection;